import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { compile } from "path-to-regexp";

import { history } from "src/navigation/store";
import {
  PAGES,
  ParsedPathname,
  ParsedSearchParams,
  Path,
  Scheme,
} from "src/types/navigation";

export interface Props extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  url: Scheme<PAGES>;
  disabled?: boolean;
}

const DELIMITER = "/~";

const allPages = Object.values(PAGES);

const isPage = (path: string) => allPages.includes(path as never);

const parsePathname = (path = ""): ParsedPathname => {
  const parts = path.split(DELIMITER).filter((item) => !!item);
  return path.startsWith(DELIMITER) ? ["/", ...parts] : parts;
};

const compilePathname = (
  prev: ParsedPathname,
  scheme: Scheme<PAGES>["scheme"],
  params: Scheme<PAGES>["params"]
): ParsedPathname => {
  const isCompilable = scheme.includes(":") || params;
  const compiled = isCompilable ? compile(scheme)(params) : scheme;
  const filtered = prev.filter((item) => item !== compiled);

  if (isPage(scheme) && filtered.length) {
    throw new Error("Use option `mode=replace`");
  }

  return [...filtered, compiled];
};

const parseQueryString = (path = ""): ParsedSearchParams =>
  path
    .slice(1)
    .split("&")
    .reduce((acc, item) => {
      const [key, value] = item.split("=");
      return key ? { ...acc, [key]: value } : acc;
    }, {});

const serializeQueryString = (params: ParsedSearchParams): string | undefined =>
  Object.keys(params).length
    ? `?${Object.entries(params)
        .map(([key, value]) => `${key}=${value}`)
        .join("&")}`
    : undefined;

export const makeHierarchicalPath = (
  from: Path | undefined,
  to: Scheme<PAGES>[],
  mode: "replace" | "detect"
): Path | null => {
  if (!to || !to.length) {
    return null;
  }

  try {
    const precompiled = to.reduce<{
      pathname: ParsedPathname;
      search: ParsedSearchParams;
    }>(
      (acc, { scheme, params, getParams }) => {
        acc.pathname = compilePathname(acc.pathname, scheme, params);

        if (getParams) {
          acc.search = { ...acc.search, ...getParams };
        }

        return acc;
      },
      mode === "replace" || (mode === "detect" && isPage(to[0].scheme))
        ? { pathname: [], search: {} }
        : {
            pathname: parsePathname(from?.pathname),
            search: parseQueryString(from?.search),
          }
    );

    return {
      pathname: precompiled.pathname.join(DELIMITER).replaceAll("//", "/"),
      search: serializeQueryString(precompiled.search),
    };
  } catch (error) {
    console.error("makeURL", error);
    return null;
  }
};

export const Link: React.FC<Props> = ({
  url,
  disabled,
  children,
  ...props
}) => {
  const to = makeHierarchicalPath(history.location, [url], "detect");

  if (!to) {
    return null;
  }

  const onClickHandler = (e: React.SyntheticEvent) => {
    if (disabled) {
      e.preventDefault();
    }
  };

  return (
    <RouterLink onClick={onClickHandler} to={to} {...props}>
      {children}
    </RouterLink>
  );
};
